import { useState } from 'react';
import clsx from 'clsx';
import {
  Globe, Image as ImageIcon, Film, ThumbsUp, MessageCircle, Share2,
  Heart, Send, Bookmark, MoreHorizontal, Smartphone, Monitor, Layers,
  Camera,
} from 'lucide-react';

/** Map of Meta CTA codes → user-facing button labels. */
export const CTA_LABEL: Record<string, string> = {
  LEARN_MORE: 'Learn More',
  SHOP_NOW: 'Shop Now',
  SIGN_UP: 'Sign Up',
  CONTACT_US: 'Contact Us',
  GET_QUOTE: 'Get Quote',
  BOOK_TRAVEL: 'Book Now',
  DOWNLOAD: 'Download',
  INSTALL_MOBILE_APP: 'Install App',
  USE_APP: 'Open App',
  WHATSAPP_MESSAGE: 'Send WhatsApp Message',
  MESSAGE_PAGE: 'Send Message',
  CALL_NOW: 'Call Now',
  ORDER_NOW: 'Order Now',
  APPLY_NOW: 'Apply Now',
  DONATE_NOW: 'Donate',
  OPEN_LINK: 'Open Link',
  WATCH_MORE: 'Watch More',
  SUBSCRIBE: 'Subscribe',
};

export type Placement = 'mobile_feed' | 'desktop_feed' | 'stories' | 'instagram_feed';

export interface CreativePreviewProps {
  pageName?: string;
  pageAvatar?: string;
  headline?: string;
  message?: string;
  description?: string;
  /** Image url, OR — for video — poster fallback used by some placements. */
  mediaUrl?: string;
  videoUrl?: string;
  /** Meta CTA code (e.g. LEARN_MORE). */
  cta?: string;
  link?: string;
}

const PLACEMENT_TABS: Array<{ value: Placement; label: string; icon: React.ReactNode }> = [
  { value: 'mobile_feed', label: 'Mobile Feed', icon: <Smartphone size={13} /> },
  { value: 'desktop_feed', label: 'Desktop', icon: <Monitor size={13} /> },
  { value: 'stories', label: 'Stories / Reels', icon: <Layers size={13} /> },
  { value: 'instagram_feed', label: 'Instagram', icon: <Camera size={13} /> },
];

function ctaLabel(cta?: string): string | null {
  if (!cta) return null;
  return CTA_LABEL[cta] ?? cta;
}

function deriveHost(link?: string): string {
  if (!link) return 'your-site.com';
  try { return new URL(link).hostname || 'your-site.com'; } catch { return link; }
}

function pickMediaSrc(p: CreativePreviewProps): string | undefined {
  // Prefer explicit mediaUrl; fall back to videoUrl (browsers render <video> w/ no poster fine)
  return p.mediaUrl || p.videoUrl;
}

export function CreativePreview(props: CreativePreviewProps) {
  const [placement, setPlacement] = useState<Placement>('mobile_feed');

  return (
    <div className="space-y-3">
      {/* Tab strip */}
      <div className="flex gap-1 rounded-xl border border-border bg-surface-alt p-1">
        {PLACEMENT_TABS.map((t) => (
          <button
            key={t.value}
            type="button"
            onClick={() => setPlacement(t.value)}
            className={clsx(
              'flex flex-1 items-center justify-center gap-1.5 rounded-lg py-2 text-xs font-semibold transition-all',
              placement === t.value
                ? 'bg-surface text-text-primary shadow-soft'
                : 'text-text-muted hover:bg-surface hover:text-text-primary',
            )}
          >
            {t.icon}
            <span className="hidden sm:inline">{t.label}</span>
          </button>
        ))}
      </div>

      {/* Mockup */}
      <div className="flex justify-center">
        {placement === 'mobile_feed' && <MobileFeedMockup {...props} />}
        {placement === 'desktop_feed' && <DesktopFeedMockup {...props} />}
        {placement === 'stories' && <StoriesMockup {...props} />}
        {placement === 'instagram_feed' && <InstagramFeedMockup {...props} />}
      </div>

      <p className="text-2xs text-text-muted text-center">
        Approximate preview — exact rendering depends on placement
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-mockups
// ─────────────────────────────────────────────────────────────────────────────

function PageHeader({ pageName, pageAvatar, dense = false }: { pageName?: string; pageAvatar?: string; dense?: boolean }) {
  const initials = (pageName ?? 'MC').slice(0, 2).toUpperCase();
  const size = dense ? 'h-8 w-8 text-xs' : 'h-10 w-10 text-sm';
  return (
    <div className="flex items-center gap-3">
      {pageAvatar ? (
        <img src={pageAvatar} alt="" className={clsx('shrink-0 rounded-full object-cover', size)} />
      ) : (
        <div className={clsx('flex shrink-0 items-center justify-center rounded-full bg-gold-gradient font-bold text-on-primary shadow-soft select-none', size)}>
          {initials}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="truncate text-[13px] font-semibold leading-tight text-text-primary">
          {pageName || 'Your Page'}
        </div>
        <div className="flex items-center gap-1.5 text-xs text-text-muted">
          <span>Sponsored</span>
          <span>·</span>
          <Globe size={10} />
        </div>
      </div>
      <MoreHorizontal size={16} className="shrink-0 text-text-muted" />
    </div>
  );
}

function MediaSlot({
  mediaUrl, videoUrl, aspect, fullBleed = false,
}: {
  mediaUrl?: string; videoUrl?: string; aspect: string; fullBleed?: boolean;
}) {
  const src = pickMediaSrc({ mediaUrl, videoUrl });
  return (
    <div className={clsx('relative w-full overflow-hidden bg-surface-alt', aspect, fullBleed && 'rounded-none')}>
      {videoUrl ? (
        <video src={videoUrl} poster={mediaUrl} className="h-full w-full object-cover" />
      ) : src ? (
        <img src={src} alt="" className="h-full w-full object-cover" />
      ) : (
        <div className="flex h-full items-center justify-center">
          {videoUrl ? <Film size={36} className="text-text-muted/40" /> : <ImageIcon size={36} className="text-text-muted/40" />}
        </div>
      )}
      {videoUrl && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-black/50 text-white">
            <svg viewBox="0 0 24 24" fill="currentColor" className="ml-0.5 h-5 w-5">
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        </div>
      )}
    </div>
  );
}

function CtaPill({ cta }: { cta?: string }) {
  const label = ctaLabel(cta);
  if (!label) return null;
  return (
    <span className="inline-flex items-center whitespace-nowrap rounded-lg border border-border bg-surface px-3 py-2 text-xs font-semibold text-text-primary shadow-soft">
      {label}
    </span>
  );
}

// ── Mobile Feed (iPhone-ish frame) ───────────────────────────────────────────
function MobileFeedMockup(p: CreativePreviewProps) {
  const host = deriveHost(p.link);
  return (
    <div className="w-full max-w-[340px] overflow-hidden rounded-[28px] border border-border bg-surface shadow-elevated">
      <div className="flex items-center justify-between px-4 pt-3 pb-2 text-2xs text-text-muted">
        <span>9:41</span>
        <span>•••</span>
      </div>
      <div className="px-4 pb-3">
        <PageHeader pageName={p.pageName} pageAvatar={p.pageAvatar} />
      </div>
      {p.message && (
        <p className="line-clamp-3 px-4 pb-3 text-sm leading-relaxed text-text-primary">{p.message}</p>
      )}
      <MediaSlot mediaUrl={p.mediaUrl} videoUrl={p.videoUrl} aspect="aspect-square" />
      <div className="flex items-center justify-between gap-3 border-t border-border bg-surface-alt/60 px-4 py-3">
        <div className="min-w-0">
          <div className="truncate text-2xs uppercase tracking-wide text-text-muted">{host}</div>
          <div className="mt-0.5 truncate text-sm font-semibold leading-tight text-text-primary">
            {p.headline || 'Your headline here'}
          </div>
          {p.description && (
            <div className="mt-0.5 truncate text-xs text-text-secondary">{p.description}</div>
          )}
        </div>
        <CtaPill cta={p.cta} />
      </div>
      <div className="flex items-center justify-around border-t border-border px-2 py-2 text-text-muted">
        <span className="flex items-center gap-1.5 text-xs"><ThumbsUp size={14} /> Like</span>
        <span className="flex items-center gap-1.5 text-xs"><MessageCircle size={14} /> Comment</span>
        <span className="flex items-center gap-1.5 text-xs"><Share2 size={14} /> Share</span>
      </div>
    </div>
  );
}

// ── Desktop Feed (wider 16:9) ────────────────────────────────────────────────
function DesktopFeedMockup(p: CreativePreviewProps) {
  const host = deriveHost(p.link);
  return (
    <div className="w-full max-w-[480px] overflow-hidden rounded-2xl border border-border bg-surface shadow-elevated">
      <div className="px-4 pt-4 pb-3">
        <PageHeader pageName={p.pageName} pageAvatar={p.pageAvatar} />
      </div>
      {p.message && (
        <p className="line-clamp-3 px-4 pb-3 text-sm leading-relaxed text-text-primary">{p.message}</p>
      )}
      <MediaSlot mediaUrl={p.mediaUrl} videoUrl={p.videoUrl} aspect="aspect-video" />
      <div className="flex items-center justify-between gap-3 border-t border-border bg-surface-alt/60 px-4 py-3">
        <div className="min-w-0">
          <div className="truncate text-2xs uppercase tracking-wide text-text-muted">{host}</div>
          <div className="mt-0.5 truncate text-sm font-semibold leading-tight text-text-primary">
            {p.headline || 'Your headline here'}
          </div>
          {p.description && (
            <div className="mt-0.5 truncate text-xs text-text-secondary">{p.description}</div>
          )}
        </div>
        <CtaPill cta={p.cta} />
      </div>
      <div className="flex items-center gap-6 border-t border-border px-4 py-2.5 text-text-muted">
        <span className="flex items-center gap-1.5 text-xs"><ThumbsUp size={14} /> Like</span>
        <span className="flex items-center gap-1.5 text-xs"><MessageCircle size={14} /> Comment</span>
        <span className="flex items-center gap-1.5 text-xs"><Share2 size={14} /> Share</span>
      </div>
    </div>
  );
}

// ── Stories / Reels (vertical 9:16, full-bleed media + bottom CTA pill) ──────
function StoriesMockup(p: CreativePreviewProps) {
  const ctaText = ctaLabel(p.cta);
  return (
    <div className="relative w-full max-w-[260px] overflow-hidden rounded-[32px] border border-border bg-black shadow-elevated">
      <div className="relative aspect-[9/16] w-full">
        {/* Full-bleed media */}
        {p.videoUrl ? (
          <video src={p.videoUrl} poster={p.mediaUrl} className="h-full w-full object-cover" />
        ) : p.mediaUrl ? (
          <img src={p.mediaUrl} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full items-center justify-center bg-surface-alt">
            <ImageIcon size={36} className="text-text-muted/40" />
          </div>
        )}

        {/* Top progress bars */}
        <div className="absolute left-3 right-3 top-3 flex gap-1">
          <div className="h-0.5 flex-1 rounded-full bg-white/80" />
          <div className="h-0.5 flex-1 rounded-full bg-white/30" />
          <div className="h-0.5 flex-1 rounded-full bg-white/30" />
        </div>

        {/* Top page header overlay */}
        <div className="absolute left-3 right-3 top-6 flex items-center gap-2">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gold-gradient text-2xs font-bold text-on-primary shadow-soft">
            {(p.pageName ?? 'MC').slice(0, 2).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-2xs font-semibold text-white">{p.pageName || 'Your Page'}</div>
            <div className="text-[10px] text-white/70">Sponsored</div>
          </div>
        </div>

        {/* Bottom gradient + headline + CTA pill */}
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/45 to-transparent px-4 pt-12 pb-5">
          {p.headline && (
            <div className="mb-3 line-clamp-2 text-sm font-semibold leading-tight text-white">
              {p.headline}
            </div>
          )}
          {ctaText && (
            <button
              type="button"
              className="flex w-full items-center justify-center gap-2 rounded-full bg-white px-4 py-2.5 text-xs font-semibold text-black shadow-soft"
            >
              {ctaText}
              <span className="text-base">›</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Instagram Feed (square media + caption format) ───────────────────────────
function InstagramFeedMockup(p: CreativePreviewProps) {
  return (
    <div className="w-full max-w-[340px] overflow-hidden rounded-2xl border border-border bg-surface shadow-elevated">
      {/* IG header */}
      <div className="flex items-center gap-3 px-3 py-2.5">
        {p.pageAvatar ? (
          <img src={p.pageAvatar} alt="" className="h-8 w-8 rounded-full object-cover" />
        ) : (
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gold-gradient text-2xs font-bold text-on-primary">
            {(p.pageName ?? 'MC').slice(0, 2).toUpperCase()}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13px] font-semibold leading-tight text-text-primary">
            {p.pageName || 'your_page'}
          </div>
          <div className="text-[10px] uppercase tracking-wide text-text-muted">Sponsored</div>
        </div>
        <MoreHorizontal size={16} className="text-text-muted" />
      </div>

      {/* Square media */}
      <MediaSlot mediaUrl={p.mediaUrl} videoUrl={p.videoUrl} aspect="aspect-square" />

      {/* Action row */}
      <div className="flex items-center justify-between px-3 pt-2.5 pb-1 text-text-primary">
        <div className="flex items-center gap-3">
          <Heart size={20} />
          <MessageCircle size={20} />
          <Send size={20} />
        </div>
        <Bookmark size={20} />
      </div>

      {/* CTA bar */}
      {ctaLabel(p.cta) && (
        <div className="flex items-center justify-between border-t border-border px-3 py-2.5">
          <span className="text-xs text-text-secondary">{deriveHost(p.link)}</span>
          <span className="text-xs font-semibold text-primary">{ctaLabel(p.cta)} ›</span>
        </div>
      )}

      {/* Caption */}
      <div className="px-3 pt-1 pb-3">
        {p.headline && (
          <div className="text-sm font-semibold text-text-primary">{p.headline}</div>
        )}
        {p.message && (
          <p className="mt-1 line-clamp-3 text-xs leading-relaxed text-text-secondary">
            <span className="font-semibold text-text-primary">{p.pageName || 'your_page'}</span>{' '}
            {p.message}
          </p>
        )}
      </div>
    </div>
  );
}
